import axios, { AxiosHeaders, AxiosInstance } from 'axios';
import {
  getLiveIdToken,
  purgePersistedSessionMirrors,
} from '../firebase/authToken.service';
import { getAnalytics } from 'src/lib/analytics/analytics.service';
import { getBootSession, recordForcedSignOut, recordTokenlessRequest, type tTokenlessAuthOutcome } from 'src/firebase/bootSession';
import { LLogMiddleware } from '../lib/logging/LLog.middleware';
import { BASE_URL_V2 } from './axios.constants';
import { tAxiosCustomConfig } from './axios.types';

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL_V2,
  // 30s default ceiling. Without this, a slow backend hangs the browser indefinitely
  // and the user is stuck on a blank Agenda forever (BUG-262). Endpoints that legitimately
  // need longer (heavy reports, exports) should override `timeout` per-call.
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// INT-1241: how long the 401 handler waits for Firebase signOut before it
// redirects anyway. Long enough for a normal local signOut (no network round
// trip), short enough that a hung SDK never strands the user on a dead page.
const SIGN_OUT_REDIRECT_TIMEOUT_MS = 2_000;

const CONFIGURATION_WRITE_PATH = /^\/?(?:ai\/workspaces\/[^/]+\/smartleadbot\/(?:config(?:\/history\/[^/]+\/restore)?|sandbox\/(?:chat|sessions))|workspace\/[^/]+\/lead-agent\/config)$/;

axiosInstance.interceptors.request.use(LLogMiddleware.request);

// BUG-801: the bearer is resolved from THIS tab's live Firebase session on
// every request, instead of the profile-global `localStorage.idToken` that a
// second account's login could overwrite (UI painting user A, requests signed
// as user B). Registered after LLogMiddleware.request so it runs BEFORE it
// (request interceptors are LIFO) and the log captures the final headers.
// An explicit per-request Authorization (e.g. the invitation-token flow in
// Workspace.service.acceptUserInvitation) always wins over the live token.
axiosInstance.interceptors.request.use(async (config) => {
  const customConfig = config as tAxiosCustomConfig;
  if (customConfig.isPublicAsset) {
    return config;
  }

  const headers = AxiosHeaders.from(config.headers);
  let authOutcome: tTokenlessAuthOutcome = 'explicit_empty_authorization';

  // Protocol freshness only: the server still verifies the actor and permissions.
  // Older bundles cannot execute a previously received direct sandbox command
  // after the Workspace Agent cutover. Native AI actions use the MCP transport.
  if (['post', 'put', 'patch'].includes(config.method?.toLowerCase() ?? '') && CONFIGURATION_WRITE_PATH.test(config.url ?? '')) {
    headers.set('X-Leadsales-Configuration-Client', 'workspace-agent-v1');
  }

  if (!headers.has('Authorization')) {
    const { token, degraded, outcome } = await getLiveIdToken();
    authOutcome = outcome ?? 'unknown';
    if (token) {
      headers.set('Authorization', token);
    }
    // A degraded bearer means Firebase never vouched for it on this call (init
    // did not settle in time, or loading the SDK failed) — see
    // authToken.service.ts. Opt this request out of the 401 auto-logout: a slow
    // boot must not escalate into wiping the session and signing the profile
    // out. The caller still sees the 401 and can retry.
    if (degraded) {
      customConfig.skipAutoRedirect = true;
      // BUG-868: skipAutoRedirect alone can't tell a caller "this 401 came from
      // a degraded boot" — ~20 services set it by hand for real 401s they handle
      // themselves. authDegraded is the unambiguous signal withAuthRetry keys on.
      customConfig.authDegraded = true;
    }
  }

  if (!headers.has('X-Api-Key')) {
    headers.set('X-Api-Key', localStorage.getItem('apiKey') || '');
  }

  config.headers = headers;
  if (!headers.get('Authorization')) recordTokenlessRequest(authOutcome);
  return config;
});

axiosInstance.interceptors.response.use(LLogMiddleware.response, (error) => {
  // LLogMiddleware.error returns Promise.reject(error) by contract. Calling it
  // as a bare statement discarded that rejection, so every axios error raised an
  // unhandledrejection alongside the one this interceptor re-throws below. The
  // no-op catch keeps the logging side effect and drops the duplicate rejection;
  // the authoritative rejection is still the `return Promise.reject(error)` at
  // the end of this handler.
  void LLogMiddleware.error(error).catch(() => undefined);

  if (error.response?.status === 401) {
    if ((error.config as tAxiosCustomConfig)?.authDegraded) getBootSession().degraded401s += 1;
    // Skip automatic redirect if the request config explicitly requests it
    // This allows specific features to handle 401 errors with custom logic
    const skipAutoRedirect = (error.config as { skipAutoRedirect?: boolean })?.skipAutoRedirect;
    
    if (!skipAutoRedirect) {
      const carriedToken = Boolean(
        AxiosHeaders.from(error.config?.headers).get('Authorization'),
      );
      // BUG-801: one shared purge list for every recovery path — see
      // authToken.service.ts. sessionStorage.clear() also drops the per-tab
      // token mirror.
      purgePersistedSessionMirrors();
      sessionStorage.clear();
      recordForcedSignOut({ carriedToken, url: error.config?.url });
      // Drops the distinct id, groups and super properties before the redirect,
      // so events on the login page are not attributed to the signed-out user.
      getAnalytics().reset();
      // INT-1241 / BUG-1116: sign Firebase out when the server rejected a
      // credential this tab sent. A tab that sent no token holds none to revoke:
      // signing it out would delete the durable credential another tab just
      // created. Its local cleanup and redirect still run.
      //
      // Firebase is pulled in dynamically, and only on this branch: a static
      // import would make every module that touches axios initialize the whole
      // Firebase app (auth + firestore + functions) at import time.
      //
      // Navigation waits for signOut because the IndexedDB delete will not land
      // once unload starts — but it waits at most SIGN_OUT_REDIRECT_TIMEOUT_MS.
      // The user is already logged out locally by the wipe above, so a slow or
      // hung Firebase must not hold them on a dead page; on timeout we redirect
      // and let the next boot's onIdTokenChanged reconcile the credential.
      void (async () => {
        try {
          if (carriedToken) await Promise.race([
            (async () => {
              const [{ signOut }, { firebase }] = await Promise.all([
                import('firebase/auth'),
                import('../firebase/firebase.singleton'),
              ]);
              await signOut(firebase.auth);
            })(),
            new Promise((resolve) =>
              setTimeout(resolve, SIGN_OUT_REDIRECT_TIMEOUT_MS),
            ),
          ]);
        } catch {
          // Best effort — the local wipe above already happened.
        }
        window.location.href = '/';
      })();
    }
  }
  return Promise.reject(error);
});
